import React, { useCallback } from 'react';
import { FixedSizeList as List } from 'react-window';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Checkbox, Radio, useMediaQuery, useTheme } from '@mui/material';
import { IGroupObject } from 'src/models/objectManagement';
import moment from 'moment';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import FilterNoneIcon from '@mui/icons-material/FilterNone';
import LibraryAddCheckIcon from '@mui/icons-material/LibraryAddCheck';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import useEcommerce from 'src/services/useEcommerce';
import { useLocation } from 'react-router';

interface Props {
  groupObjects: Array<IGroupObject>;
  loading?: boolean;
  // groupSelectedById: string | undefined;
  onClick: (group: IGroupObject) => void;
  initialSelectedGroups: IGroupObject[];
}

const ROW_HEIGHT = 53;
const HEADER_HEIGHT = 56;

type ColumnAlignment = 'inherit' | 'left' | 'center' | 'right' | 'justify';
type ColumnWidth = number | 'auto';

interface ColumnConfig {
  id: string;
  width: ColumnWidth;
  label?: string;
  align?: ColumnAlignment;
}

const COLUMNS: readonly ColumnConfig[] = [
  { id: 'radio', width: 60 },
  { id: 'no', width: 60, label: 'No' },
  { id: 'group', width: 'auto', label: 'Group' },
  { id: 'added', width: 100, label: 'Added', align: 'center' },
  { id: 'count', width: 100, label: 'Object Count', align: 'center' }
] as const;

interface VirtualizedTableBodyProps {
  items: Array<IGroupObject>;
  // groupSelectedById: string | undefined;
  onRowClick: (group: IGroupObject) => void;
  selectedGroups: IGroupObject[];
}

const VirtualizedTableBody: React.FC<VirtualizedTableBodyProps> = ({
  items,
  selectedGroups,
  onRowClick
}) => {
  const theme = useTheme();
  const { pathname } = useLocation();
  const isEcommerce = pathname.includes('ecommerce');
  const projectId = useAppSelector((state) => state.users.project.id);
  const { groupObject } = useAppSelector((state) => state.objectManagement);
  const matchesUpMd = useMediaQuery(theme.breakpoints.up('md'));

  const { useGetEcommerceGroups } = useEcommerce();

  const { data: ecommerceGroupData } = useGetEcommerceGroups({
    variables: {
      projectId,
      templateName: 'content',
      objectGroupType: 'ecommerce'
    }
  });

  const groupList = isEcommerce
    ? ecommerceGroupData?.objectManagement_getObjectGroupList ?? []
    : groupObject.data;

  const Row = useCallback(
    ({ index, style }: { index: number; style: React.CSSProperties }) => {
      const row = items[index];

      const isGroupSelected = () => {
        if (row.id === 'all') return selectedGroups.length === groupList.length;

        return selectedGroups.some((group) => group?.id === row.id);
      };

      const isIndeterminate = () => {
        if (row.id === 'all') {
          return (
            selectedGroups.length > 0 &&
            selectedGroups.length < groupList.length
          );
        }
        return false;
      };

      return (
        <TableRow
          hover
          onClick={() => onRowClick(row)}
          style={{
            ...style,
            display: 'table',
            tableLayout: 'fixed',
            width: '100%'
          }}
        >
          <TableCell width={COLUMNS[0].width}>
            <Checkbox
              checked={isGroupSelected()}
              indeterminate={isIndeterminate()}
              value={row.id}
              name="group-object"
              size="small"
              sx={{ color: theme.palette.primary.main }}
            />
          </TableCell>
          <TableCell width={COLUMNS[1].width}>{index + 1}</TableCell>
          <TableCell>
            {!matchesUpMd && row.displayName?.length > 35
              ? row.displayName?.slice(0, 35) + '...'
              : row.displayName}
          </TableCell>
          <TableCell width={COLUMNS[3].width} align="center">
            {row.createdAt ? moment(row.createdAt).format('D/M/YY') : '-'}
          </TableCell>
          <TableCell width={COLUMNS[4].width} align="center">
            {row.id === 'all' ? '-' : row.objectList.length}
          </TableCell>
        </TableRow>
      );
    },
    [items, selectedGroups, onRowClick, theme.palette]
  );

  return (
    <List
      height={parseInt(theme.spacing(30)) - HEADER_HEIGHT}
      itemCount={items.length}
      itemSize={ROW_HEIGHT}
      width="100%"
    >
      {Row}
    </List>
  );
};

const TableGroupObject: React.FC<Props> = ({
  groupObjects,
  loading = false,
  // groupSelectedById,
  onClick,
  initialSelectedGroups
}) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const handleClick = (group: IGroupObject) => {
    onClick(group);
  };

  const matchesUpMd = useMediaQuery(theme.breakpoints.up('md'));

  return (
    <TableContainer
      component={Paper}
      sx={{ maxHeight: theme.spacing(30), maxWidth: 900 }}
    >
      <Table
        style={{ tableLayout: 'fixed', width: matchesUpMd ? '100%' : '500px' }}
      >
        <TableHead>
          <TableRow
            style={{
              display: 'table',
              width: '100%'
              // tableLayout: 'fixed'
            }}
          >
            {COLUMNS.map((column) => (
              <TableCell
                key={column.id}
                align={column.align}
                width={column.width !== 'auto' ? column.width : undefined}
              >
                {column.label || ''}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <VirtualizedTableBody
            items={groupObjects}
            // groupSelectedById={groupSelectedById}
            onRowClick={handleClick}
            selectedGroups={initialSelectedGroups}
          />
        </TableBody>
      </Table>
    </TableContainer>
  );
};

interface IPropsTableGroupObjectWithCheckbox {
  groupObjects: Array<IGroupObject>;
  loading?: boolean;
  // groupSelectedById: string | undefined;
  onClick: (group: IGroupObject[]) => void;
  initialSelectedGroups: IGroupObject[];
}

const TableGroupObjectWithCheckbox: React.FC<IPropsTableGroupObjectWithCheckbox> =
  ({
    groupObjects,
    loading = false,
    // groupSelectedById,
    onClick,
    initialSelectedGroups
  }) => {
    const theme = useTheme();
    const dispatch = useAppDispatch();

    const [selectedGroups, setSelectedGroups] = React.useState<IGroupObject[]>(
      initialSelectedGroups
    );

    const handleClick = (group: IGroupObject) => {
      const isGroupSelected = selectedGroups.some((g) => g.id === group.id);

      setSelectedGroups((prev) => {
        const data = isGroupSelected
          ? prev.filter((g) => g.id !== group.id)
          : [...prev, group];

        onClick(data);
        return data;
      });
    };

    const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
      let groupSelected: IGroupObject[] = [];
      if (event.target.checked) {
        groupSelected = groupObjects;
      } else {
        groupSelected = [];
      }
      setSelectedGroups(groupObjects);
      onClick(groupObjects);
    };

    // Hitung jumlah item yang dipilih
    const numSelected = selectedGroups.length;
    const rowCount = groupObjects.length;

    const matchesUpMd = useMediaQuery(theme.breakpoints.up('md'));

    return (
      <TableContainer
        component={Paper}
        sx={{ maxHeight: theme.spacing(30), maxWidth: 900 }}
      >
        <Table
          style={{
            tableLayout: 'fixed',
            width: matchesUpMd ? '100%' : '500px'
          }}
        >
          <TableHead>
            <TableRow
              style={{
                display: 'table',
                width: '100%'
                // tableLayout: 'fixed'
              }}
            >
              {COLUMNS.map((column) => {
                if (column.id == 'radio') {
                  return (
                    <TableCell sx={{ width: 60 }} padding="none" align="center">
                      <Checkbox
                        indeterminate={
                          numSelected > 0 && numSelected < rowCount
                        }
                        checked={rowCount > 0 && numSelected === rowCount}
                        size="small"
                        icon={
                          <FilterNoneIcon
                            sx={{
                              color: theme.palette.common.white,
                              width: theme.spacing(2.5),
                              height: theme.spacing(2.5)
                            }}
                          />
                        }
                        checkedIcon={
                          <LibraryAddCheckIcon
                            sx={{
                              color: theme.palette.common.white,
                              width: theme.spacing(2.5),
                              height: theme.spacing(2.5)
                            }}
                          />
                        }
                        indeterminateIcon={
                          <IndeterminateCheckBoxIcon
                            sx={{
                              color: theme.palette.common.white,
                              width: theme.spacing(2.5),
                              height: theme.spacing(2.5)
                            }}
                          />
                        }
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                  );
                }
                return (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    width={column.width !== 'auto' ? column.width : undefined}
                  >
                    {column.label || ''}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            <VirtualizedTableBody
              items={groupObjects}
              // groupSelectedById={groupSelectedById}
              onRowClick={handleClick}
              selectedGroups={selectedGroups}
            />
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

export { TableGroupObjectWithCheckbox };
export default TableGroupObject;
